import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { apiJsonDedup } from "../utils/api";

export type PaginatedItems<T> = {
  items: T[];
  total: number;
};

type PaginationMeta = {
  page: number;
  limit: number;
  total: number;
  pages: number;
  hasNext: boolean;
  hasPrev: boolean;
};

type PaginatedApiResponse<T> = {
  data: T[];
  pagination: PaginationMeta;
};

export interface UsePaginatedListOptions {
  endpoint: string; // e.g. "admin/v1/properties"
  page?: number;
  limit?: number;
  enabled?: boolean;
  pageParamName?: string;  // default 'page'
  limitParamName?: string; // default 'limit'
  offsetParamName?: string; // optional: use offset instead of page
  extraParams?: Record<string, string>; // additional query parameters
}

export interface UsePaginatedListResult<TItem> {
  items: TItem[];
  total: number;
  page: number;
  limit: number;
  setPage: (n: number) => void;
  setLimit: (n: number) => void;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function usePaginatedList<TItem = unknown>(
  options: UsePaginatedListOptions
): UsePaginatedListResult<TItem> {
  const {
    endpoint,
    page: initialPage = 1,
    limit: initialLimit = 10,
    enabled = true,
    pageParamName = "page",
    limitParamName = "limit",
    offsetParamName,
    extraParams = {},
  } = options;

  const [page, setPage] = useState<number>(initialPage);
  const [limit, setLimit] = useState<number>(initialLimit);
  const [items, setItems] = useState<TItem[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const reqId = useRef(0);

  const queryString = useMemo(() => {
    const p = new URLSearchParams();

    if (offsetParamName) {
      const offset = (page - 1) * limit;
      p.set(offsetParamName, String(Math.max(0, offset)));
      p.set(limitParamName, String(limit));
    } else {
      p.set(pageParamName, String(page));
      p.set(limitParamName, String(limit));
    }

    // Add extra parameters
    Object.entries(extraParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        p.set(key, String(value));
      }
    });

    return p.toString();
  }, [page, limit, pageParamName, limitParamName, offsetParamName, extraParams]);

  const refetch = useCallback(async () => {
    if (!enabled) return;

    const id = ++reqId.current;
    setLoading(true);
    setError(null);

    try {
      const resp = await apiJsonDedup<PaginatedApiResponse<TItem>>(
        `${endpoint}?${queryString}`
      );

      const nextItems = Array.isArray(resp?.data) ? resp.data : [];
      const nextTotal = typeof resp?.pagination?.total === "number" ? resp.pagination.total : 0;

      if (reqId.current === id) {
        setItems(nextItems);
        setTotal(nextTotal);
      }
    } catch (e: any) {
      if (reqId.current === id) {
        setError(e?.message ?? "Failed to load data");
        setItems([]);
        setTotal(0);
      }
    } finally {
      if (reqId.current === id) setLoading(false);
    }
  }, [endpoint, queryString, enabled]);

  useEffect(() => {
    if (!enabled) return;
    void refetch();
  }, [refetch, enabled]);

  return {
    items,
    total,
    page,
    limit,
    setPage,
    setLimit,
    loading,
    error,
    refetch,
  };
}